import { useDispatch, useSelector } from "react-redux";
import {
  closeSidebar,
  toggleSidebar,
  toggleSidebarCollapsed,
} from "../app/sidebarSlice";
import { logout } from "../features/authSlice";
import { useWallet } from "../context/walletContext";
import React, { useState, useEffect, useRef } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { formatAmount } from "../utils/formatAmount";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BrandLogo from "../utils/logoImg";
import { useNotify } from "../context/NotifyContext";

import { logoutMerchantUser } from "../api/userAuth";
import ThemeToggle from "./ThemeToggle";
import ConfirmDialog from "./ui/ConfirmDialog";
import { showToastMessage } from "../utils/toastUtils";

const Navbar = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { walletAmt } = useWallet();
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { collapsed, isOpen } = useSelector((state) => state.sidebar);
    const [logoutOpen, setLogoutOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);  const [anchorEl, setAnchorEl] = useState(null);

  const { notify } = useNotify();

  // const handleLogout = async () => {
  //   try {
  //     await logoutMerchantUser();
  //   } catch (err) {
  //     console.error("Logout API failed", err);
  //   }
  //   dispatch(logout());
  //   notify("Logged out successfully!", "success");
  //   navigate("/");
  // };

    const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutMerchantUser();
    } catch {
      /* still clear local session */
    } finally {
      dispatch(logout());
      setLoggingOut(false);
      setLogoutOpen(false);
      showToastMessage("success", "Logged out successfully");
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleUserDropdown = () => {
    setMenuOpen((prev) => {
      if (!prev) dispatch(closeSidebar());
      return !prev;
    });
  };

  /** One control: desktop = collapse rail, mobile = open/close drawer */
  const handleSidebarToggle = () => {
    setMenuOpen(false);
    if (isDesktop) dispatch(toggleSidebarCollapsed());
    else dispatch(toggleSidebar());
  };

  const balance = walletAmt?.main?.balance || 0;
  const showBrandInHeader = !isDesktop || collapsed;
  const toggleLabel = isDesktop
    ? collapsed
      ? "Show sidebar"
      : "Hide sidebar"
    : isOpen
    ? "Close menu"
    : "Open menu";

  return (
    <>
    <AppBar
      position="sticky"
      elevation={0}
      color="inherit"
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid",
        borderColor: "divider",
        height: 60,
        justifyContent: "center",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: "60px !important",
          height: 60,
          px: { xs: 3.5, sm: 2 },
          gap: 1,
        }}
      >
        <Tooltip title={toggleLabel}>
          <IconButton
            edge="start"
            onClick={handleSidebarToggle}
            aria-label={toggleLabel}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "var(--radius-md)",
              width: 36,
              height: 36,
            }}
          >
            {isDesktop ? (
              collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />
            ) : (
              <MenuIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

       {showBrandInHeader && (
  <Box
    onClick={() => navigate("/")}
    sx={{
      display: "flex",
      alignItems: "center",
      ml: 0.5,
      cursor: "pointer",
    }}
  >
    <BrandLogo
      variant={theme.palette.mode === "dark" ? "white" : "blue"}
      size="sm"
    />
  </Box>
)}

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative" }}
          ref={dropdownRef}
        >
          <Chip
            size="small"
            label={`₹ ${formatAmount(balance)}`}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              fontWeight: 600,
              bgcolor: "action.hover",
              borderRadius: "var(--radius-sm)",
              height: 32,
            }}
          />

          <IconButton
            aria-label="Notifications"
            size="small"
            sx={{
              display: { xs: "none", md: "inline-flex" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "var(--radius-md)",
              width: 36,
              height: 36,
            }}
          >
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </IconButton>

          <ThemeToggle size="small" />

          <Box
            onClick={handleUserDropdown}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "var(--radius-md)",
              pl: 0.5,
              pr: { xs: 0.5, sm: 1 },
              py: 0.35,
              minHeight: 36,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Avatar
              src={
                userInfo?.logo
                  ? `${userInfo.logo}`
                  : undefined
              }
              sx={{ width: 28, height: 28, fontSize: 13 }}
            >
              {userInfo?.name?.[0]}
            </Avatar>
            <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0, maxWidth: 140 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.15, fontSize: 13 }}
                noWrap
              >
                {userInfo?.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.1, fontSize: 11 }}
                noWrap
              >
                {userInfo?.userName}
              </Typography>
            </Box>
            <KeyboardArrowDownRoundedIcon
              sx={{ display: { xs: "none", md: "block" }, fontSize: 18, color: "text.secondary" }}
            />
          </Box>

          {menuOpen && (
            <Paper
              className="ds-panel"
              sx={{
                position: "absolute",
                right: 6,
                top: 40,
                width: 240,
                p: 1.5,
                zIndex: 50,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, pb: 1.25 }}>
                <Avatar sx={{ width: 36, height: 36 }}>{userInfo?.name?.[0]}</Avatar>
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {userInfo?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {userInfo?.userName}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 1.25,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <span>Wallet</span>
                <span>₹ {formatAmount(balance)}</span>
              </Box>
              <Divider />
              <button
                type="button"
             onClick={() => {
                              setAnchorEl(null);
                              setLogoutOpen(true);
                            }}
                            sx={{ color: "error.main" }}
                          
                className="w-full text-left font-semibold py-2 mt-1 text-[var(--error-color)] text-[13px]"
              >
                   <LogoutOutlinedIcon fontSize="small" sx={{ mr: 1.25 }} />
                            Logout
              </button>
          
            </Paper>
          )}
        </Box>
      </Toolbar>
    </AppBar>
      <ConfirmDialog
        open={logoutOpen}
        title="Sign out"
        message="Are you sure you want to logout from the admin panel?"
        confirmLabel="Logout"
        confirmColor="error"
        loading={loggingOut}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={handleLogout}
      />
    </>
  );
};

export default Navbar;
